import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { SymbolService, MarketStatusService, BinaryFeedService, ISymbolService, IMarketStatusService, IFeedService } from '@smartcharts/core';
// Phase 7 indicators integration
// Indicators imported from chart-web root exports
import { IndicatorManager, PaneManager, IndicatorController } from '@smartcharts/chart-web';
import { LightweightChartEngine, FlutterChartEngine, IChartEngine, ChartDataCoordinator } from '@smartcharts/chart-web';
import { binaryApi, tickFormatter } from './binaryFeedAdapters';
import { DERIV_APP_ID } from './config';
import { ReplayController } from './replay/ReplayController';
import { TQuote } from '@smartcharts/core';

// DERIV_APP_ID imported from config

export type EngineProviderProps = {
  children: React.ReactNode;
  symbol?: string;
  granularity?: number;
  useLightweight?: boolean; // feature flag
  initialLocale?: string;
};

export interface EngineContextValue {
  engine: IChartEngine;
  coordinator: ChartDataCoordinator;
  replay?: ReplayController;
  symbolService: ISymbolService;
  marketStatusService: IMarketStatusService;
  feedService: IFeedService;
  currentSymbol?: string;
  setSymbol: (sym: string, granularity?: number) => Promise<void>;
  setGranularity: (granularity: number) => Promise<void>;
  changeLocale: (locale: string) => Promise<void>;
  indicatorManager: IndicatorManager;
  indicatorController: IndicatorController;
  paneManager: PaneManager;
}

const EngineContext = createContext<EngineContextValue | undefined>(undefined);
export const useEngineContext = () => {
  const ctx = useContext(EngineContext);
  if (!ctx) throw new Error('useEngineContext must be used within EngineProvider');
  return ctx;
};

export const EngineProvider: React.FC<EngineProviderProps> = ({ children, symbol = 'R_100', granularity = 60, useLightweight = true, initialLocale = 'en' }) => {
  const [currentSymbol, setCurrentSymbol] = useState(symbol);
  const [isInitialized, setIsInitialized] = useState(false);
  const engineRef = useRef<IChartEngine>();
  const coordinatorRef = useRef<ChartDataCoordinator>();
  const symbolServiceRef = useRef<ISymbolService>();
  const marketStatusRef = useRef<IMarketStatusService>();
  const feedRef = useRef<IFeedService>();
  const indicatorManagerRef = useRef<IndicatorManager>();
  const indicatorControllerRef = useRef<IndicatorController>();
  const paneManagerRef = useRef<PaneManager>();
  const replayRef = useRef<ReplayController>();

  // Shared WS request helper with simple retry/backoff
  const wsRequest = async <T,>(request: any, pick: (data: any) => T | undefined, timeoutMs = 15000, retries = 1): Promise<T> => {
    const endpoints = [
      'wss://ws.derivws.com/websockets/v3',
      'wss://ws.binaryws.com/websockets/v3'
    ];
    let endpointIndex = 0;
    let attempt = 0;
    const start = async (): Promise<T> => new Promise<T>((resolve, reject) => {
      const url = `${endpoints[endpointIndex]}?app_id=${DERIV_APP_ID}`;
      let opened = false;
      const ws = new WebSocket(url);
      let timer: number | undefined = window.setTimeout(() => {
        try { ws.close(); } catch {}
        reject(new Error('Timeout'));
      }, timeoutMs);
      ws.onopen = () => {
        opened = true;
        try { ws.send(JSON.stringify(request)); } catch (e) { reject(e as Error); }
      };
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.error) {
            if (timer) clearTimeout(timer);
            try { ws.close(); } catch {}
            reject(new Error(data.error.message));
            return;
          }
          const picked = pick(data);
          if (picked !== undefined) {
            if (timer) clearTimeout(timer);
            try { ws.close(); } catch {}
            resolve(picked);
          }
        } catch {
          /* ignore */
        }
      };
      ws.onerror = () => {
        if (timer) clearTimeout(timer);
        reject(new Error('WebSocket error'));
      };
      ws.onclose = () => {
        if (!opened) {
          // Immediate close before open: try next endpoint if available
          if (endpointIndex + 1 < endpoints.length) {
            endpointIndex++;
            // Chain a new attempt immediately
            wsRequest(request, pick, timeoutMs, retries).then(resolve).catch(reject);
          }
        }
      };
    });
    while (attempt <= retries) {
      try {
        return await start();
      } catch (e) {
        attempt++;
        if (attempt > retries) throw e as Error;
        await new Promise(r => setTimeout(r, 400 * attempt));
      }
    }
    throw new Error('Unexpected');
  };

  // Init services/engine once
  useEffect(() => {
    const init = async () => {
      try {
        // Expose app id for adapters (binaryfeedadapters)
        (window as any).DERIV_APP_ID = DERIV_APP_ID;
      // Simple single-shot fallback helper
      const fallbackRequest = async (req: any, pick: (d: any) => any) => {
        return new Promise<any>((resolve, reject) => {
          const ws = new WebSocket(`wss://ws.binaryws.com/websockets/v3?app_id=${DERIV_APP_ID}`);
          let timer: number | undefined = window.setTimeout(() => { try { ws.close(); } catch {}; reject(new Error('fallback timeout')); }, 8000);
          ws.onopen = () => { try { ws.send(JSON.stringify(req)); } catch (e) { reject(e as Error); } };
          ws.onmessage = evt => { try { const data = JSON.parse(evt.data); if (data.error) { if (timer) clearTimeout(timer); try { ws.close(); } catch {}; reject(new Error(data.error.message)); return; } const picked = pick(data); if (picked) { if (timer) clearTimeout(timer); try { ws.close(); } catch {}; resolve(picked); } } catch {/* ignore */} };
          ws.onerror = () => { if (timer) clearTimeout(timer); reject(new Error('fallback ws error')); };
          ws.onclose = () => { /* ignore */ };
        });
      };
      // Create marketStatus first so symbolService can reference it
      const marketStatus = new MarketStatusService({ fetchTradingTimes: async (date?: string) => {
        const requestDate = date || new Date().toISOString().split('T')[0];
        try {
          return await wsRequest({ trading_times: requestDate }, d => d.trading_times);
        } catch (e) {
          console.warn('[EngineProvider] trading_times primary endpoints failed', e);
          try { return await fallbackRequest({ trading_times: requestDate }, d => d.trading_times); } catch (e2) {
            console.error('[EngineProvider] trading_times fallback failed', e2); return { markets: [] }; }
        }
      }, getServerTime: () => Math.floor(Date.now()/1000) });
      const symbolService = new SymbolService({ fetchActiveSymbols: async () => {
        try {
          return await wsRequest({ active_symbols: 'brief', product_type: 'basic' }, d => d.active_symbols);
        } catch (e) {
          console.warn('[EngineProvider] active_symbols primary endpoints failed', e);
          try { const res = await fallbackRequest({ active_symbols: 'brief', product_type: 'basic' }, d => d.active_symbols); if (res && res.length) return res; } catch (e2) {
            console.error('[EngineProvider] active_symbols fallback failed', e2); }
          // Final stub to allow chart to proceed
          return [{
            symbol: symbol,
            display_name: symbol,
            market: 'synthetic_index',
            market_display_name: 'Synthetic',
            subgroup: 'random_index',
            subgroup_display_name: 'Random Index',
            submarket_display_name: 'Random',
            exchange_is_open: true,
            pip: 0.01,
          }];
        }
      }, locale: initialLocale, marketStatusService: marketStatus });
      await symbolService.init();
      await marketStatus.init();

      const feed = new BinaryFeedService({ api: binaryApi, formatter: tickFormatter, isFeedUnavailable: s => !symbolService.isMarketOpen(s), getDelayedMinutes: s => symbolService.getDelayedMinutes(s) || 0 });
      const engine = useLightweight ? new LightweightChartEngine() : new FlutterChartEngine();

        // Initialize indicators managers BEFORE coordinator so we can inject
        const paneManager = new PaneManager();
        const indicatorManager = new IndicatorManager(paneManager);
        const indicatorController = new IndicatorController({ manager: indicatorManager });
        const coordinator = new ChartDataCoordinator({ feedService: feed, engine, symbolService, indicatorManager, onError: (err) => {
          console.error('[EngineProvider] Coordinator error:', err);
        }});

      symbolServiceRef.current = symbolService;
      marketStatusRef.current = marketStatus;
      feedRef.current = feed;
      engineRef.current = engine;
      coordinatorRef.current = coordinator;
      paneManagerRef.current = paneManager;
      indicatorManagerRef.current = indicatorManager;
      indicatorControllerRef.current = indicatorController;
      // Setup replay capture after engine instantiated
      const capturedQuotes: TQuote[] = [];
      const eng = engineRef.current!;
      const originalApplyBatch = eng.applyTickBatch.bind(eng);
      eng.applyTickBatch = (quotes: TQuote[], append?: boolean) => {
        if (quotes?.length) {
          if (!append) capturedQuotes.length = 0;
          for (const q of quotes) capturedQuotes.push(q);
        }
        originalApplyBatch(quotes, append);
      };
      const originalApplyTick = eng.applyTick.bind(eng);
      eng.applyTick = (quote: TQuote, replace?: boolean) => {
        capturedQuotes.push(quote);
        originalApplyTick(quote, replace);
      };
      replayRef.current = new ReplayController({
        engine: eng,
        getQuotes: () => capturedQuotes,
        onRestoreLive: () => {
          // Restore all captured live data when exiting replay
          if (capturedQuotes.length) {
            eng.resetSeries();
            eng.applyTickBatch([...capturedQuotes], false);
          }
        },
      });

      // Shim legacy flutterChart indicators API when using lightweight engine
      if (useLightweight) {
        const w = window as any;
        w.flutterChart = w.flutterChart || {};
        w.flutterChart.indicators = w.flutterChart.indicators || {};
        w.flutterChart.indicators.removeIndicator = (index: number) => {
          const items = indicatorManager.listIndicators();
          const target = items[index];
          if (target) indicatorManager.removeIndicator(target.id);
        };
        w.flutterChart.indicators.clearIndicators = () => {
          indicatorManager.removeAll();
        };
        // Shim legacy drawing tools API expected by SmartChart
        // Some SmartChart code calls flutterChart.clearDrawingTool() directly (not via .drawings)
        w.flutterChart.clearDrawingTool = () => {
          try { engine.clearDrawingTool(); } catch {}
        };
        w.flutterChart.removeDrawingTool = (index: number) => {
          try { engine.removeDrawingTool(index); } catch {}
        };
        // Also provide nested drawings namespace for compatibility
        w.flutterChart.drawings = w.flutterChart.drawings || {};
        w.flutterChart.drawings.clearDrawingTool = () => {
          try { engine.clearDrawingTool(); } catch {}
        };
        w.flutterChart.drawings.removeDrawingTool = (index: number) => {
          try { engine.removeDrawingTool(index); } catch {}
        };
        // And the drawingTool namespace used by ChartState.clearLayout()
        w.flutterChart.drawingTool = w.flutterChart.drawingTool || {};
        w.flutterChart.drawingTool.clearDrawingTool = () => {
          try { engine.clearDrawingTool(); } catch {}
        };
        w.flutterChart.drawingTool.clearDrawingToolSelect = () => {
          // no-op for lightweight engine
        };
        w.flutterChart.drawingTool.removeDrawingTool = (index: number) => {
          try { engine.removeDrawingTool(index); } catch {}
        };
        // Config namespace for market state, remaining time, crosshair visibility
        w.flutterChart.config = w.flutterChart.config || {};
        w.flutterChart.config.setSymbolClosed = (isClosed: boolean) => {
          try { engine.setSymbolClosed?.(isClosed); } catch {}
        };
        w.flutterChart.config.setRemainingTime = (_time: string) => {
          // no-op for lightweight engine
        };
        w.flutterChart.config.updateCrosshairVisibility = (_visible: boolean) => {
          // no-op for lightweight engine
        };
        w.flutterChart.config.updateLiveStatus = (_isLive: boolean) => {
          // no-op for lightweight engine
        };
      }

      // Initial symbol/series setup
      console.log('[EngineProvider] Initializing chart for', symbol, 'granularity', granularity);
      await coordinator.setSymbolAndGranularity({ symbol: { symbol, name: symbol, decimalPlaces: symbolService.getDecimalPlaces(symbol) || 2, exchange_is_open: !!symbolService.isMarketOpen(symbol) }, granularity });
      console.log('[EngineProvider] Initial symbol set, state:', coordinator.getState());
      
      // Mark as initialized to trigger re-render
      setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize chart environment:', error);
      }
    };
    init();
  }, []);

  const setSymbol = async (sym: string, g?: number) => {
    if (!coordinatorRef.current || !symbolServiceRef.current) return;
    const meta = symbolServiceRef.current.getSymbol(sym);
    if (!meta) return;
    await coordinatorRef.current.setSymbolAndGranularity({ symbol: { symbol: meta.symbol, name: meta.name, decimalPlaces: meta.decimal_places, exchange_is_open: !!meta.exchange_is_open }, granularity: g });
    setCurrentSymbol(sym);
  };
  const setGranularity = async (g: number) => {
    if (!coordinatorRef.current) return;
    const state = coordinatorRef.current.getState();
    if (!state.currentSymbol) return;
    await coordinatorRef.current.setSymbolAndGranularity({ symbol: state.currentSymbol, granularity: g });
  };
  const changeLocale = async (locale: string) => {
    if (!coordinatorRef.current) return;
    await coordinatorRef.current.onLocaleChanged(locale);
  };

  if (!isInitialized || !engineRef.current || !coordinatorRef.current || !symbolServiceRef.current || !marketStatusRef.current || !feedRef.current || !indicatorManagerRef.current || !indicatorControllerRef.current || !paneManagerRef.current) {
    return <div style={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', fontSize: '16px', color: '#333' }}>Loading chart environment…</div>;
  }

  return (
    <EngineContext.Provider value={{
      engine: engineRef.current,
      coordinator: coordinatorRef.current,
      symbolService: symbolServiceRef.current,
      marketStatusService: marketStatusRef.current,
      feedService: feedRef.current,
      currentSymbol,
      setSymbol,
      setGranularity,
      changeLocale,
      indicatorManager: indicatorManagerRef.current,
      indicatorController: indicatorControllerRef.current,
      paneManager: paneManagerRef.current,
      replay: replayRef.current,
    }}>
      {children}
    </EngineContext.Provider>
  );
};

export default EngineProvider;
